import React, { useState, useRef, Fragment } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Patient, Encounter } from '../../types';
import { Search, FileText, Printer, Trash2, Download } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
export const PatientFiles: React.FC = () => {
  const {
    patients,
    encounters,
    deletePatient,
    currentUser
  } = useData();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const printableRef = useRef<HTMLDivElement>(null);
  const filteredPatients = patients.filter(p => p.firstName.toLowerCase().includes(searchQuery.toLowerCase()) || p.lastName.toLowerCase().includes(searchQuery.toLowerCase()) || p.bmcFileNumber.toLowerCase().includes(searchQuery.toLowerCase()));
  const getPatientEncounters = (patientId: string) => {
    return encounters.filter(e => e.patientId === patientId).sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime());
  };
  const handleDeletePatient = (id: string) => {
    if (window.confirm('WARNING: This will permanently delete the patient and ALL associated medical records. Are you sure?')) {
      deletePatient(id);
      setSelectedPatient(null);
    }
  };
  const handlePrint = () => {
    window.print();
  };
  const handleDownloadPdf = async () => {
    if (!printableRef.current || !selectedPatient) return;
    setIsGeneratingPdf(true);
    try {
      const filename = `BMED_PatientFile_${selectedPatient.bmcFileNumber}_${selectedPatient.firstName}_${selectedPatient.lastName}.pdf`;
      // A4 dimensions in cm
      const pageWidthCm = 21.0;
      const pageHeightCm = 29.7;
      const marginCm = 1.5;
      const contentWidthCm = pageWidthCm - marginCm * 2;
      const contentHeightCm = pageHeightCm - marginCm * 2;
      const canvas = await html2canvas(printableRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false
      });
      const pdf = new jsPDF({
        unit: 'cm',
        format: 'a4',
        orientation: 'portrait',
        compress: true
      });
      const imgPropsHeightCm = canvas.height * contentWidthCm / canvas.width;
      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      if (imgPropsHeightCm <= contentHeightCm) {
        pdf.addImage(imgData, 'JPEG', marginCm, marginCm, contentWidthCm, imgPropsHeightCm);
      } else {
        let remainingHeightCm = imgPropsHeightCm;
        let positionCm = marginCm;
        while (remainingHeightCm > 0) {
          pdf.addImage(imgData, 'JPEG', marginCm, positionCm, contentWidthCm, imgPropsHeightCm);
          remainingHeightCm -= contentHeightCm;
          if (remainingHeightCm > 0) {
            pdf.addPage();
            positionCm = marginCm - (imgPropsHeightCm - remainingHeightCm);
          }
        }
      }
      pdf.save(filename);
    } catch (err) {
      console.error('PDF generation failed', err);
      alert('Failed to generate PDF. Please try again or use the Print option.');
    } finally {
      setIsGeneratingPdf(false);
    }
  };
  // ─── PATIENT FILE VIEW ───
  if (selectedPatient) {
    const patientEncounters = getPatientEncounters(selectedPatient.id);
    return <div className="space-y-6" data-id="element-119">
        {/* Action Bar */}
        <div className="flex justify-between items-center print:hidden" data-id="element-120">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-121">Patient File</h1>
          <div className="flex space-x-2" data-id="element-122">
            <Button variant="outline" onClick={() => setSelectedPatient(null)} data-id="element-123">
              Back to Directory
            </Button>
            <Button variant="outline" onClick={handlePrint} data-id="element-124">
              <Printer className="w-4 h-4 mr-2" data-id="element-125" /> Print
            </Button>
            <Button variant="primary" onClick={handleDownloadPdf} disabled={isGeneratingPdf} data-id="element-126">
              <Download className="w-4 h-4 mr-2" data-id="element-127" />
              {isGeneratingPdf ? 'Generating PDF...' : 'Download PDF'}
            </Button>
            {currentUser?.role === 'Administrator' && <Button variant="danger" onClick={() => handleDeletePatient(selectedPatient.id)} data-id="element-128">
                <Trash2 className="w-4 h-4 mr-2" data-id="element-129" /> Delete File
              </Button>}
          </div>
        </div>

        {/* Encounter Status Summary (screen only) */}
        <div className="print:hidden bg-white rounded-lg shadow-sm p-4 border border-gray-200" data-id="element-130">
          <h3 className="text-sm font-semibold text-gray-700 mb-2" data-id="element-131">
            Encounter History ({patientEncounters.length})
          </h3>
          <div className="flex flex-wrap gap-2" data-id="element-132">
            {patientEncounters.map((enc, idx) => <span key={enc.id} className={`px-3 py-1 rounded-full text-xs font-medium ${enc.stage === 'COMPLETED' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-133">
                Visit {patientEncounters.length - idx}:{' '}
                {new Date(enc.startedAt).toLocaleDateString()} •{' '}
                {enc.stage === 'COMPLETED' ? '✓ Completed' : enc.stage}
              </span>)}
          </div>
        </div>

        {/* All Encounters – Always Expanded — wrapped in ref for PDF capture */}
        <div ref={printableRef} data-id="element-134">
          {patientEncounters.length > 0 ? patientEncounters.map((enc, index) => <div key={enc.id} className={`bg-white border-2 border-gray-300 rounded-lg p-6 mb-6 ${index > 0 ? 'print:break-before-page pdf-page-break' : ''}`} data-id="element-135">
                {/* ═══ FORM HEADER ═══ */}
                <div className="flex justify-between items-start mb-6" data-id="element-136">
                  <div className="flex items-center" data-id="element-137">
                    <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED Medical Centre" className="h-20 w-20 object-contain mr-4" data-id="element-138" />
                    <div data-id="element-139">
                      <h1 className="text-2xl font-bold text-pink-600 uppercase tracking-wider" data-id="element-140">
                        BMED Medical Centre
                      </h1>
                      <p className="text-sm italic text-gray-600" data-id="element-141">
                        Maternity Care Made Better
                      </p>
                    </div>
                  </div>
                  <div className="text-right" data-id="element-142">
                    <p className="font-bold text-lg uppercase tracking-widest border-b-2 border-black pb-1 mb-2" data-id="element-143">
                      Confidential
                    </p>
                    <p className="text-sm" data-id="element-144">
                      <span className="font-semibold" data-id="element-145">Date:</span>{' '}
                      {new Date(enc.startedAt).toLocaleDateString()}
                    </p>
                    <p className="text-sm" data-id="element-146">
                      <span className="font-semibold" data-id="element-147">File No:</span>{' '}
                      {selectedPatient.bmcFileNumber}
                    </p>
                  </div>
                </div>

                {/* ═══ PERSONAL DETAILS ═══ */}
                <div className="border-2 border-black p-4 mb-6 text-sm space-y-3" data-id="element-148">
                  <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-149">
                    <div className="col-span-1 font-semibold" data-id="element-150">1.</div>
                    <div className="col-span-6" data-id="element-151">
                      <span className="font-semibold" data-id="element-152">Full Names:</span>{' '}
                      {selectedPatient.firstName} {selectedPatient.lastName}
                    </div>
                    <div className="col-span-3" data-id="element-153">
                      <span className="font-semibold" data-id="element-154">Sex:</span>{' '}
                      {selectedPatient.sex}
                    </div>
                    <div className="col-span-2" data-id="element-155">
                      <span className="font-semibold" data-id="element-156">Age:</span>{' '}
                      {selectedPatient.age}
                    </div>
                  </div>
                  <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-157">
                    <div className="col-span-1 font-semibold" data-id="element-158">2.</div>
                    <div className="col-span-11" data-id="element-159">
                      <span className="font-semibold" data-id="element-160">Marital Status:</span>{' '}
                      {selectedPatient.maritalStatus}
                    </div>
                  </div>
                  <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-161">
                    <div className="col-span-1 font-semibold" data-id="element-162">3.</div>
                    <div className="col-span-7" data-id="element-163">
                      <span className="font-semibold" data-id="element-164">Home Address:</span>{' '}
                      {selectedPatient.address}
                    </div>
                    <div className="col-span-4" data-id="element-165">
                      <span className="font-semibold" data-id="element-166">Contact:</span>{' '}
                      {selectedPatient.contact}
                    </div>
                  </div>
                  <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-167">
                    <div className="col-span-1 font-semibold" data-id="element-168">4.</div>
                    <div className="col-span-11" data-id="element-169">
                      <span className="font-semibold" data-id="element-170">Occupation:</span>{' '}
                      {selectedPatient.occupation || '_________________________'}
                    </div>
                  </div>
                  <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-171">
                    <div className="col-span-1 font-semibold" data-id="element-172">5.</div>
                    <div className="col-span-5" data-id="element-173">
                      <span className="font-semibold" data-id="element-174">Next of Kin:</span>{' '}
                      {selectedPatient.nextOfKin}
                    </div>
                    <div className="col-span-3" data-id="element-175">
                      <span className="font-semibold" data-id="element-176">Contact:</span>{' '}
                      {selectedPatient.nokContact}
                    </div>
                    <div className="col-span-3" data-id="element-177">
                      <span className="font-semibold" data-id="element-178">Relationship:</span>{' '}
                      {selectedPatient.nokRelationship}
                    </div>
                  </div>
                  <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-179">
                    <div className="col-span-1 font-semibold" data-id="element-180">6.</div>
                    <div className="col-span-11" data-id="element-181">
                      <span className="font-semibold" data-id="element-182">Allergies:</span>{' '}
                      <span className="text-red-600 font-medium" data-id="element-183">
                        {selectedPatient.allergies}
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-12 gap-2" data-id="element-184">
                    <div className="col-span-1 font-semibold" data-id="element-185">7.</div>
                    <div className="col-span-11" data-id="element-186">
                      <span className="font-semibold" data-id="element-187">HIV Status:</span>{' '}
                      <span className="ml-4" data-id="element-188">
                        <span className={`inline-block px-2 py-0.5 border border-black mr-3 ${selectedPatient.hivStatus === 'Positive' ? 'bg-red-100 font-bold' : ''}`} data-id="element-189">
                          Positive{' '}
                          {selectedPatient.hivStatus === 'Positive' ? '✓' : ''}
                        </span>
                        <span className={`inline-block px-2 py-0.5 border border-black mr-3 ${selectedPatient.hivStatus === 'Negative' ? 'bg-green-100 font-bold' : ''}`} data-id="element-190">
                          Negative{' '}
                          {selectedPatient.hivStatus === 'Negative' ? '✓' : ''}
                        </span>
                        <span className={`inline-block px-2 py-0.5 border border-black ${selectedPatient.hivStatus === 'Unknown' ? 'bg-yellow-100 font-bold' : ''}`} data-id="element-191">
                          Unknown{' '}
                          {selectedPatient.hivStatus === 'Unknown' ? '✓' : ''}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>

                {/* ═══ NURSING OBSERVATIONS ═══ */}
                <div className="mb-6" data-id="element-192">
                  <h3 className="font-bold uppercase bg-gray-200 p-2 border-2 border-black border-b-0 text-center" data-id="element-193">
                    Nursing Observations
                  </h3>
                  <table className="w-full border-collapse border-2 border-black text-sm text-center" data-id="element-194">
                    <thead data-id="element-195">
                      <tr className="bg-gray-50" data-id="element-196">
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => <th key={n} className="border border-black p-1 text-xs text-gray-500" data-id="element-197">
                            {n}
                          </th>)}
                      </tr>
                      <tr className="bg-gray-100" data-id="element-198">
                        <th className="border border-black p-1" data-id="element-199">Temp</th>
                        <th className="border border-black p-1" data-id="element-200">Pulse</th>
                        <th className="border border-black p-1" data-id="element-201">Resp</th>
                        <th className="border border-black p-1" data-id="element-202">BP</th>
                        <th className="border border-black p-1" data-id="element-203">SpO2</th>
                        <th className="border border-black p-1" data-id="element-204">Weight (Kg)</th>
                        <th className="border border-black p-1" data-id="element-205">Height</th>
                        <th className="border border-black p-1" data-id="element-206">BMI</th>
                        <th className="border border-black p-1" data-id="element-207">Pain Score</th>
                        <th className="border border-black p-1" data-id="element-208">AVPU</th>
                      </tr>
                    </thead>
                    <tbody data-id="element-209">
                      <tr data-id="element-210">
                        <td className="border border-black p-2 font-medium" data-id="element-211">
                          {enc.vitals?.temp || ''}
                        </td>
                        <td className="border border-black p-2 font-medium" data-id="element-212">
                          {enc.vitals?.pulse || ''}
                        </td>
                        <td className="border border-black p-2 font-medium" data-id="element-213">
                          {enc.vitals?.resp || ''}
                        </td>
                        <td className="border border-black p-2 font-medium" data-id="element-214">
                          {enc.vitals?.bp || ''}
                        </td>
                        <td className="border border-black p-2 font-medium" data-id="element-215">
                          {enc.vitals?.sp02 || ''}
                        </td>
                        <td className="border border-black p-2 font-medium" data-id="element-216">
                          {enc.vitals?.weight || ''}
                        </td>
                        <td className="border border-black p-2 font-medium" data-id="element-217">
                          {enc.vitals?.height || ''}
                        </td>
                        <td className="border border-black p-2 font-medium" data-id="element-218">
                          {enc.vitals?.bmi || ''}
                        </td>
                        <td className="border border-black p-2 font-medium" data-id="element-219">
                          {enc.vitals?.painScore || ''}
                        </td>
                        <td className="border border-black p-2 font-medium" data-id="element-220">
                          {enc.vitals?.avpu || ''}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* ═══ CLINICAL NOTES ═══ */}
                <div className="space-y-5 mb-6 text-sm" data-id="element-221">
                  <div className="border-2 border-black" data-id="element-222">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-223">
                      Presenting Complaint
                    </h3>
                    <div className="p-3 min-h-[80px] whitespace-pre-wrap" data-id="element-224">
                      {enc.clinicalNote?.presentingComplaint || ''}
                    </div>
                  </div>

                  <div className="border-2 border-black" data-id="element-225">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-226">
                      History of Presenting Complaint
                    </h3>
                    <div className="p-3 min-h-[80px] whitespace-pre-wrap" data-id="element-227">
                      {enc.clinicalNote?.historyOfPresentingComplaint || ''}
                    </div>
                  </div>

                  {/* PAST MEDICAL HISTORY */}
                  <div className="border-2 border-black" data-id="element-228">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-229">
                      Past Medical History / General Co-Morbidities
                    </h3>
                    <table className="w-full border-collapse text-sm" data-id="element-230">
                      <tbody data-id="element-231">
                        {['DIABETES', 'EPILEPSY', 'TUBERCULOSIS', 'HYPERTENSION', 'ASTHMA', 'SICKLE CELL DISEASE', 'ANEMIA', 'RVD'].map(condition => <tr key={condition} className="border-b border-gray-300" data-id="element-232">
                            <td className="border-r border-gray-300 p-2 font-medium w-48" data-id="element-233">
                              {condition}
                            </td>
                            <td className="p-2 text-center" data-id="element-234">
                              {enc.clinicalNote?.pastMedicalHistory?.includes(condition) ? '✓ Yes' : ''}
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>

                  {/* DRUG HISTORY */}
                  <div className="border-2 border-black" data-id="element-235">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-236">
                      Drug History
                    </h3>
                    <div className="p-3 min-h-[40px]" data-id="element-237">
                      {enc.clinicalNote?.drugHistory || ''}
                    </div>
                    <div className="p-3 border-t border-gray-300" data-id="element-238">
                      <span className="font-semibold" data-id="element-239">Any known allergy:</span>{' '}
                      <span className="text-red-600 font-medium" data-id="element-240">
                        {selectedPatient.allergies}
                      </span>
                    </div>
                  </div>

                  {/* FAMILY MEDICAL HISTORY */}
                  <div className="border-2 border-black" data-id="element-241">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-242">
                      Family Medical History
                    </h3>
                    <table className="w-full border-collapse text-sm" data-id="element-243">
                      <tbody data-id="element-244">
                        {['DIABETES', 'EPILEPSY', 'TUBERCULOSIS', 'HYPERTENSION', 'ASTHMA', 'SICKLE CELL DISEASE', 'ANEMIA'].map(condition => <tr key={condition} className="border-b border-gray-300" data-id="element-245">
                            <td className="border-r border-gray-300 p-2 font-medium w-48" data-id="element-246">
                              {condition}
                            </td>
                            <td className="p-2 text-center" data-id="element-247">
                              {enc.clinicalNote?.familyMedicalHistory?.includes(condition) ? '✓ Yes' : ''}
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>

                  {/* OBSTETRICS AND GYNAECOLOGY HISTORY */}
                  <div className="border-2 border-black" data-id="element-248">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-249">
                      Obstetrics and Gynaecology History
                    </h3>
                    <div className="p-3 min-h-[60px] whitespace-pre-wrap" data-id="element-250">
                      {enc.clinicalNote?.obgynHistory || ''}
                    </div>
                  </div>

                  {/* SOCIAL HISTORY */}
                  <div className="border-2 border-black" data-id="element-251">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-252">
                      Social History
                    </h3>
                    <table className="w-full border-collapse text-sm" data-id="element-253">
                      <tbody data-id="element-254">
                        <tr className="border-b border-gray-300" data-id="element-255">
                          <td className="border-r border-gray-300 p-2 font-medium w-48" data-id="element-256">
                            Alcohol
                          </td>
                          <td className="p-2" data-id="element-257">
                            {enc.clinicalNote?.socialHistory?.alcohol || ''}
                          </td>
                        </tr>
                        <tr className="border-b border-gray-300" data-id="element-258">
                          <td className="border-r border-gray-300 p-2 font-medium" data-id="element-259">
                            Smoking
                          </td>
                          <td className="p-2" data-id="element-260">
                            {enc.clinicalNote?.socialHistory?.smoking || ''}
                          </td>
                        </tr>
                        <tr className="border-b border-gray-300" data-id="element-261">
                          <td className="border-r border-gray-300 p-2 font-medium" data-id="element-262">
                            Occupation
                          </td>
                          <td className="p-2" data-id="element-263">
                            {enc.clinicalNote?.socialHistory?.occupation || ''}
                          </td>
                        </tr>
                        <tr data-id="element-264">
                          <td className="border-r border-gray-300 p-2 font-medium" data-id="element-265">
                            Mental Status
                          </td>
                          <td className="p-2" data-id="element-266">
                            {enc.clinicalNote?.socialHistory?.mentalStatus || ''}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* REVIEW OF SYSTEMS */}
                  <div className="border-2 border-black" data-id="element-267">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-268">
                      Review of Systems
                    </h3>
                    <div className="p-3 space-y-1" data-id="element-269">
                      <p data-id="element-270">
                        <span className="font-semibold" data-id="element-271">R/S:</span>{' '}
                        {enc.clinicalNote?.reviewOfSystems?.rs || ''}
                      </p>
                      <p data-id="element-272">
                        <span className="font-semibold" data-id="element-273">CVS:</span>{' '}
                        {enc.clinicalNote?.reviewOfSystems?.cvs || ''}
                      </p>
                      <p data-id="element-274">
                        <span className="font-semibold" data-id="element-275">CNS:</span>{' '}
                        {enc.clinicalNote?.reviewOfSystems?.cns || ''}
                      </p>
                      <p data-id="element-276">
                        <span className="font-semibold" data-id="element-277">GIT:</span>{' '}
                        {enc.clinicalNote?.reviewOfSystems?.git || ''}
                      </p>
                      <p data-id="element-278">
                        <span className="font-semibold" data-id="element-279">GUT:</span>{' '}
                        {enc.clinicalNote?.reviewOfSystems?.gut || ''}
                      </p>
                      <p data-id="element-280">
                        <span className="font-semibold" data-id="element-281">MSS:</span>{' '}
                        {enc.clinicalNote?.reviewOfSystems?.mss || ''}
                      </p>
                      <p data-id="element-282">
                        <span className="font-semibold" data-id="element-283">IS:</span>{' '}
                        {enc.clinicalNote?.reviewOfSystems?.is || ''}
                      </p>
                    </div>
                  </div>

                  {/* ON EXAMINATION */}
                  <div className="border-2 border-black" data-id="element-284">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-285">
                      On Examination
                    </h3>
                    <div className="p-3 min-h-[80px] whitespace-pre-wrap" data-id="element-286">
                      {enc.clinicalNote?.onExamination || 'G/C – Stable / Ill Looking / Unconscious\n- Febrile / Afebrile / Lethargic\n- P|J|C\n\nS1  S2 : -\n\nR/S  : -\n\nP/A'}
                    </div>
                  </div>

                  {/* SUMMARY OF SIGNIFICANT FINDINGS */}
                  <div className="border-2 border-black" data-id="element-287">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-288">
                      Summary of Significant Findings
                    </h3>
                    <div className="p-3 min-h-[60px]" data-id="element-289">
                      {enc.clinicalNote?.significantFindings?.filter(f => f.trim()).length ? <ol className="list-decimal pl-5 space-y-1" data-id="element-290">
                          {enc.clinicalNote.significantFindings.filter(f => f.trim()).map((finding, i) => <li key={i} data-id="element-291">{finding}</li>)}
                        </ol> : <div className="space-y-2 text-gray-400" data-id="element-292">
                          {[1, 2, 3, 4, 5, 6, 7, 8].map(n => <p key={n} data-id="element-293">
                              {n}.
                              ______________________________________________________________________________________
                            </p>)}
                        </div>}
                    </div>
                  </div>

                  {/* WORKING DIAGNOSIS */}
                  <div className="border-2 border-black" data-id="element-294">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-295">
                      Working Diagnosis
                    </h3>
                    <div className="p-3 min-h-[60px]" data-id="element-296">
                      {enc.clinicalNote?.workingDiagnosis?.filter(d => d.trim()).length ? <ol className="list-decimal pl-5 space-y-1" data-id="element-297">
                          {enc.clinicalNote.workingDiagnosis.filter(d => d.trim()).map((dx, i) => <li key={i} className="font-bold" data-id="element-298">
                                {dx}
                              </li>)}
                        </ol> : <div className="space-y-2 text-gray-400" data-id="element-299">
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => <p key={n} data-id="element-300">
                              {n}.
                              ______________________________________________________________________________________
                            </p>)}
                        </div>}
                    </div>
                  </div>

                  {/* INVESTIGATIONS TO BE DONE */}
                  <div className="border-2 border-black" data-id="element-301">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-302">
                      Investigations To Be Done
                    </h3>
                    <div className="p-4" data-id="element-303">
                      <table className="w-full border-collapse text-sm mb-4" data-id="element-304">
                        <tbody data-id="element-305">
                          {[['FBC', 'GRAVINDEX'], ['MPS/RDT', 'HVS'], ['CREATINE / U&A', 'URINALYSIS'], ['LIVER FUNCTION TEST', 'PAP SMEAR'], ['AAFB', 'CD4 COUNT'], ['URINE MICROSCOPY', 'URETHRAL SWAB'], ['VBG', 'VIRAL LOAD'], ['HEPATITIS PROFILE', 'RBS / FBS'], ['ABG', 'STOOL MICROSCOPY'], ['LIVER FUNCTION TEST', 'DOA 6'], ['CXR', 'RPR'], ['HIV', 'SCAN/ULTRA SOUND'], ['SPERM ANALYSIS', 'TESTOSTERONE']].map((pair, idx) => <tr key={idx} className="border-b border-gray-200" data-id="element-306">
                              {pair.map(test => {
                        const isOrdered = enc.labRequests.some(lab => lab.tests.includes(test)) || test === 'SCAN/ULTRA SOUND' && enc.radiologyRequests.length > 0;
                        return <Fragment key={test} data-id="element-307">
                                    <td className="p-1.5 font-medium border-r border-gray-200 w-1/3" data-id="element-308">
                                      {test}
                                    </td>
                                    <td className="p-1.5 w-1/6 text-center border-r border-gray-300" data-id="element-309">
                                      {isOrdered ? <span className="text-green-700 font-bold" data-id="element-310">
                                          ✓
                                        </span> : ''}
                                    </td>
                                  </Fragment>;
                      })}
                            </tr>)}
                        </tbody>
                      </table>

                      <div className="border-t border-gray-300 pt-2" data-id="element-311">
                        <span className="font-semibold" data-id="element-312">
                          Other investigations:
                        </span>{' '}
                        {enc.labRequests.map(l => l.otherTests).filter(Boolean).join(', ') || ''}
                      </div>

                      {/* Investigation Results */}
                      {(enc.labRequests.some(l => l.status === 'Completed') || enc.radiologyRequests.some(r => r.status === 'Completed')) && <div className="mt-4 pt-4 border-t-2 border-black" data-id="element-313">
                          <h4 className="font-bold mb-3 uppercase text-center" data-id="element-314">
                            Investigation Results
                          </h4>

                          {enc.labRequests.filter(l => l.status === 'Completed' && l.results).map(lab => <table key={lab.id} className="w-full border-collapse text-sm mb-3" data-id="element-315">
                                <tbody data-id="element-316">
                                  {Object.entries(lab.results!).map(([t, r]) => <tr key={t} className="border-b border-gray-200" data-id="element-317">
                                        <td className="p-1.5 font-medium w-1/3 border-r border-gray-200" data-id="element-318">
                                          {t}
                                        </td>
                                        <td className="p-1.5" data-id="element-319">{r}</td>
                                      </tr>)}
                                </tbody>
                              </table>)}

                          {enc.radiologyRequests.filter(r => r.status === 'Completed').map(rad => <div key={rad.id} className="mb-4 border border-gray-300 rounded p-3" data-id="element-320">
                                <p className="font-bold mb-1" data-id="element-321">{rad.scanType}</p>
                                {rad.radiographerName && <p className="text-xs text-gray-500 mb-2" data-id="element-322">
                                    Radiographer: {rad.radiographerName}
                                  </p>}
                                {rad.report && <div className="mb-2" data-id="element-323">
                                    <span className="font-semibold" data-id="element-324">
                                      Report:
                                    </span>
                                    <p className="whitespace-pre-wrap mt-1" data-id="element-325">
                                      {rad.report}
                                    </p>
                                  </div>}
                                {rad.images && rad.images.length > 0 && <div data-id="element-326">
                                    <span className="font-semibold" data-id="element-327">
                                      Scan Images:
                                    </span>
                                    <div className="flex gap-3 mt-2 overflow-x-auto print:flex-wrap" data-id="element-328">
                                      {rad.images.map((img, idx) => <img key={idx} src={img} alt={`${rad.scanType} ${idx + 1}`} className="h-28 w-28 object-cover rounded border-2 border-gray-300 cursor-pointer hover:border-blue-500 transition-colors print:h-36 print:w-36" onClick={() => window.open(img, '_blank')} data-id="element-329" />)}
                                    </div>
                                  </div>}
                              </div>)}
                        </div>}
                    </div>
                  </div>

                  {/* DRUG TREATMENT / PRESCRIPTION */}
                  <div className="border-2 border-black" data-id="element-330">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-331">
                      Drug Treatment / Prescription
                    </h3>
                    <table className="w-full border-collapse text-sm text-center" data-id="element-332">
                      <thead data-id="element-333">
                        <tr className="bg-gray-100" data-id="element-334">
                          <th className="border border-black p-1.5 w-10" data-id="element-335">
                            No.
                          </th>
                          <th className="border border-black p-1.5 text-left" data-id="element-336">
                            Drug Name
                          </th>
                          <th className="border border-black p-1.5" data-id="element-337">Dose</th>
                          <th className="border border-black p-1.5" data-id="element-338">Route</th>
                          <th className="border border-black p-1.5" data-id="element-339">
                            Frequency
                          </th>
                          <th className="border border-black p-1.5" data-id="element-340">
                            No. of Days
                          </th>
                          <th className="border border-black p-1.5" data-id="element-341">
                            Signature
                          </th>
                        </tr>
                      </thead>
                      <tbody data-id="element-342">
                        {enc.prescriptions.length > 0 ? enc.prescriptions.map((rx, i) => <tr key={rx.id} data-id="element-343">
                                <td className="border border-black p-1.5" data-id="element-344">
                                  {i + 1}
                                </td>
                                <td className="border border-black p-1.5 text-left font-medium" data-id="element-345">
                                  {rx.drugName}
                                </td>
                                <td className="border border-black p-1.5" data-id="element-346">
                                  {rx.dose}
                                </td>
                                <td className="border border-black p-1.5" data-id="element-347">
                                  {rx.route}
                                </td>
                                <td className="border border-black p-1.5" data-id="element-348">
                                  {rx.frequency}
                                </td>
                                <td className="border border-black p-1.5" data-id="element-349">
                                  {rx.days}
                                </td>
                                <td className="border border-black p-1.5 italic text-xs" data-id="element-350">
                                  {enc.lastAttendingClinician || ''}
                                </td>
                              </tr>) : [1, 2, 3, 4, 5, 6, 7].map(i => <tr key={i} data-id="element-351">
                                <td className="border border-black p-3" data-id="element-352">{i}</td>
                                <td className="border border-black p-3" data-id="element-353"></td>
                                <td className="border border-black p-3" data-id="element-354"></td>
                                <td className="border border-black p-3" data-id="element-355"></td>
                                <td className="border border-black p-3" data-id="element-356"></td>
                                <td className="border border-black p-3" data-id="element-357"></td>
                                <td className="border border-black p-3" data-id="element-358"></td>
                              </tr>)}
                      </tbody>
                    </table>
                  </div>

                  {/* STAT DOSES ADMINISTERED BY NURSING */}
                  {enc.statDoses && enc.statDoses.length > 0 && <div className="border-2 border-black" data-id="element-359">
                      <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-360">
                        Stat Doses Administered (Nursing)
                      </h3>
                      <table className="w-full border-collapse text-sm" data-id="element-361">
                        <thead data-id="element-362">
                          <tr className="bg-gray-100" data-id="element-363">
                            <th className="border border-black p-1.5 w-10" data-id="element-364">
                              No.
                            </th>
                            <th className="border border-black p-1.5 text-left" data-id="element-365">
                              Drug Name
                            </th>
                            <th className="border border-black p-1.5 text-left" data-id="element-366">
                              Dose
                            </th>
                            <th className="border border-black p-1.5 text-left" data-id="element-367">
                              Route
                            </th>
                            <th className="border border-black p-1.5 text-left" data-id="element-368">
                              Time Given
                            </th>
                            <th className="border border-black p-1.5 text-left" data-id="element-369">
                              Nurse
                            </th>
                          </tr>
                        </thead>
                        <tbody data-id="element-370">
                          {enc.statDoses.map((dose, idx) => <tr key={dose.id} data-id="element-371">
                              <td className="border border-black p-1.5 text-center" data-id="element-372">
                                {idx + 1}
                              </td>
                              <td className="border border-black p-1.5 font-medium" data-id="element-373">
                                {dose.drugName}
                              </td>
                              <td className="border border-black p-1.5" data-id="element-374">
                                {dose.dose}
                              </td>
                              <td className="border border-black p-1.5" data-id="element-375">
                                {dose.route}
                              </td>
                              <td className="border border-black p-1.5" data-id="element-376">
                                {dose.givenAt ? new Date(dose.givenAt).toLocaleString() : ''}
                              </td>
                              <td className="border border-black p-1.5 italic text-xs" data-id="element-377">
                                {dose.nurseName || ''}
                              </td>
                            </tr>)}
                        </tbody>
                      </table>
                    </div>}

                  {/* FOLLOW UP / REVIEW DATE */}
                  <div className="border-2 border-black" data-id="element-378">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-379">
                      Next Follow Up / Review Date (If Any)
                    </h3>
                    <table className="w-full border-collapse text-sm text-center" data-id="element-380">
                      <thead data-id="element-381">
                        <tr className="bg-gray-100" data-id="element-382">
                          <th className="border border-black p-1 w-10" data-id="element-383">No.</th>
                          <th className="border border-black p-1" data-id="element-384">Date</th>
                          <th className="border border-black p-1 w-10" data-id="element-385">No.</th>
                          <th className="border border-black p-1" data-id="element-386">Date</th>
                          <th className="border border-black p-1 w-10" data-id="element-387">No.</th>
                          <th className="border border-black p-1" data-id="element-388">Date</th>
                        </tr>
                      </thead>
                      <tbody data-id="element-389">
                        {[0, 1, 2, 3, 4].map(row => <tr key={row} data-id="element-390">
                            <td className="border border-black p-1 font-medium" data-id="element-391">
                              {row + 1}
                            </td>
                            <td className="border border-black p-2" data-id="element-392">
                              {row === 0 && enc.reviewDate ? new Date(enc.reviewDate).toLocaleDateString() : ''}
                            </td>
                            <td className="border border-black p-1 font-medium" data-id="element-393">
                              {row + 6}
                            </td>
                            <td className="border border-black p-2" data-id="element-394"></td>
                            <td className="border border-black p-1 font-medium" data-id="element-395">
                              {row + 11}
                            </td>
                            <td className="border border-black p-2" data-id="element-396"></td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>

                  {/* NAME OF MEDICAL PRACTITIONER */}
                  <div className="border-2 border-black p-4 mt-6" data-id="element-397">
                    <p className="font-bold text-sm" data-id="element-398">
                      Name of the Medical Practitioner:{' '}
                      <span className="font-normal border-b border-black inline-block min-w-[250px] ml-2" data-id="element-399">
                        {enc.lastAttendingClinician || ''}
                      </span>
                    </p>
                  </div>
                </div>
              </div>) : <Card data-id="element-400">
              <p className="text-gray-500 text-center py-8" data-id="element-401">
                No encounters found for this patient.
              </p>
            </Card>}
        </div>
      </div>;
  }
  // ─── PATIENT DIRECTORY ───
  return <div className="space-y-6" data-id="element-402">
      <div className="flex justify-between items-center" data-id="element-403">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-404">Patient Directory</h1>
      </div>

      <Card data-id="element-405">
        <div className="relative" data-id="element-406">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-407">
            <Search className="h-5 w-5 text-gray-400" data-id="element-408" />
          </div>
          <input type="text" className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="Search by name or BMC number..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-409" />
        </div>
      </Card>

      <Card noPadding data-id="element-410">
        <div className="overflow-x-auto" data-id="element-411">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-412">
            <thead className="bg-gray-50" data-id="element-413">
              <tr data-id="element-414">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-415">
                  BMC Number
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-416">
                  Patient Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-417">
                  Age/Sex
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-418">
                  Registered
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-419">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-420">
              {filteredPatients.length > 0 ? filteredPatients.map(patient => <tr key={patient.id} className="hover:bg-gray-50" data-id="element-421">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600" data-id="element-422">
                      {patient.bmcFileNumber}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-423">
                      {patient.firstName} {patient.lastName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-424">
                      {patient.age} / {patient.sex}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-425">
                      {new Date(patient.registeredAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-426">
                      <Button variant="outline" size="sm" onClick={() => setSelectedPatient(patient)} data-id="element-427">
                        <FileText className="w-4 h-4 mr-2" data-id="element-428" /> View File
                      </Button>
                    </td>
                  </tr>) : <tr data-id="element-429">
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-500" data-id="element-430">
                    No patients found matching your search.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>;
};